// src/services/datev/DatevEmployeeService.ts

import { supabase } from '../../shared/services/supabase';
import { netlifyFetch } from '../../shared/services/netlifyFetch';

/**
 * App-Identifier für Multi-App-OAuth.
 * Jeder Wert entspricht einer eigenständigen DATEV-App mit eigener Client-ID/Secret.
 */
export type DatevApp = 'payrollreports' | 'exports' | 'exchange' | 'eau';

export interface DatevEmployee {
  customer_id: string;
  beraternummer: string;
  mandantennummer: string;
  personalnummer: string;
  nachname: string;
  vorname: string;
  geburtsdatum: string | null;
  email: string | null;
  eintrittsdatum: string;
  austrittsdatum: string | null;
  created_at: string;
  updated_at: string;
}

export interface SyncResult {
  success: boolean;
  employeesCount?: number;
  newEmployees?: number;
  updatedEmployees?: number;
  deletedEmployees?: number;
  error?: string;
}

export interface DatevConnectionStatus {
  connected: boolean;
  accessTokenValid?: boolean;
  refreshTokenValid?: boolean;
  refreshTokenExpiresAt?: string | null;
  tokenIssuedBy?: string | null;
  scope?: string | null;
  sandboxMode?: boolean;
  lastUpdated?: string | null;
  datevTokenManagementUrl?: string;
  authorizedClients?: AuthorizedClientsResponse | null;
  authorizedClientsFetchedAt?: string | null;
  // Permission-Probe-Felder (Audit 22.05.2026). Werden vom datev-auth-Backend
  // nach dem Token-Exchange gesetzt. Wenn permissionProbeStatus 'denied' oder
  // 'error' ist, hat der Stick-Login keinen Zugriff auf die Berater-Mandant-
  // Kombination und die Tokens sind nicht in der DB gespeichert.
  permissionProbeStatus?: 'granted' | 'denied' | 'error' | null;
  permissionProbeAt?: string | null;
  permissionProbeHttpStatus?: number | null;
  permissionProbeErrorCode?: string | null;
  permissionProbeErrorMessage?: string | null;
  permissionProbeHelpUrl?: string | null;
  // Beraternummer/Mandantennummer aus dem datev_auth-Datensatz (Backend liefert
  // sie im /status-Response). Wird fuer die UI-Anzeige bei Berechtigungsproblemen
  // verwendet.
  beraternummer?: string | null;
  mandantennummer?: string | null;
}

/**
 * Eine Gruppe freigegebener DATEV-Bestände: alle Mandantennummern eines Beraters,
 * komprimiert als Bereiche (z.B. ["1 bis 5", "10", "22 bis 25"]).
 * Wird durch datev-{app}/clients-Endpoint serverseitig erzeugt.
 */
export interface AuthorizedClientsGroup {
  consultant: number;
  ranges: string[];
  count: number;
}

export interface AuthorizedClientsResponse {
  groups: AuthorizedClientsGroup[];
  extraction_error?: string;
}

/**
 * Error-Klasse fuer DATEV-API-Fehler. Trägt die DATEV-Hilfs-URL (error_uri),
 * den DATEV-Error-Code und ggf. einen Help-Text mit, damit das UI sie
 * anzeigen kann (DATEV-MUST: "4XXer Fehlercodes sind zu behandeln, mindestens
 * die Hilfs-URL ist im UI anzuzeigen").
 *
 * Wird geworfen aus den Service-Methoden, wenn das Backend einen 4XX/5XX
 * mit DATEV-Error-Daten zurueckgibt. Aufrufer koennen via instanceof
 * differenzieren und helpUrl, datevErrorCode rendern.
 */
export class DatevApiError extends Error {
  helpUrl: string | null;
  datevErrorCode: string | null;
  httpStatus: number | null;

  constructor(message: string, helpUrl: string | null = null, datevErrorCode: string | null = null, httpStatus: number | null = null) {
    super(message);
    this.name = 'DatevApiError';
    this.helpUrl = helpUrl;
    this.datevErrorCode = datevErrorCode;
    this.httpStatus = httpStatus;
  }
}

/**
 * Liest das standardisierte DATEV-Backend-Error-Format aus einer Response.
 * Backend (datev-auth.js, datev-payrollreports.js) liefert bei 4XX/5XX:
 *   { error: "...", message: "...", datev_error: "#DCO...", help_url: "..." }
 */
async function parseBackendErrorResponse(response: Response): Promise<DatevApiError> {
  let body: { error?: string; message?: string; datev_error?: string; help_url?: string | null };
  try {
    body = await response.json();
  } catch {
    body = { error: `HTTP ${response.status}` };
  }
  const message = body.message || body.error || `HTTP ${response.status}`;
  return new DatevApiError(message, body.help_url ?? null, body.datev_error ?? null, response.status);
}

class DatevEmployeeService {
  // Verwende absolute URL statt relative URL
  private getApiBaseUrl(): string {
    // Nutze immer die korrekte Origin - hardcoded für Sicherheit
    return 'https://intranet.xtax.de/.netlify/functions/datev-auth';
  }

  /**
   * Generiert die DATEV OAuth Authorization URL über das Backend.
   *
   * Bei Backend-Fehlern (z.B. fehlende Beraternummer/Mandantennummer in den
   * Stammdaten, ungültiger App-Identifier, DB-Fehler) wird ein DatevApiError
   * geworfen, der die Backend-Message + ggf. DATEV-Hilfs-URL trägt.
   */
  async getAuthorizationUrl(customerId: string, app: DatevApp, mode?: 'popup'): Promise<string> {
    const modeParam = mode === 'popup' ? '&mode=popup' : '';
    const response = await netlifyFetch(`${this.getApiBaseUrl()}/authorize?customerId=${customerId}&app=${app}${modeParam}`);

    if (!response.ok) {
      throw await parseBackendErrorResponse(response);
    }

    const data = await response.json();
    return data.authUrl;
  }

  /**
   * Tauscht den Authorization Code gegen Access Token.
   *
   * Returns true bei Erfolg. Bei DATEV-Fehlern (z.B. abgelaufener Code, falscher
   * State, Token-Validation-Fehler) wird ein DatevApiError geworfen, der die
   * DATEV-Hilfs-URL traegt (DATEV-MUST: "4XXer Fehlercodes sind zu behandeln,
   * mindestens die Hilfs-URL ist im UI anzuzeigen").
   */
  async handleAuthCallback(code: string, customerId: string, app: DatevApp): Promise<boolean> {
    const response = await netlifyFetch(`${this.getApiBaseUrl()}/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code, customerId, app })
    });

    if (!response.ok) {
      throw await parseBackendErrorResponse(response);
    }

    return true;
  }

  /**
   * Prüft ob eine DATEV-Verbindung für die angegebene App besteht und liefert Statusdetails
   */
  async getConnectionStatus(customerId: string, app: DatevApp): Promise<DatevConnectionStatus> {
    try {
      const response = await netlifyFetch(`${this.getApiBaseUrl()}/status?customerId=${customerId}&app=${app}`);
      
      if (!response.ok) {
        return { connected: false };
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking connection status:', error);
      return { connected: false };
    }
  }

  /**
   * Prüft ob eine DATEV-Verbindung für die angegebene App besteht (Kompatibilität)
   */
  async isConnected(customerId: string, app: DatevApp): Promise<boolean> {
    const status = await this.getConnectionStatus(customerId, app);
    return status.connected;
  }

  /**
   * Trennt die DATEV-Verbindung für die angegebene App
   * MUST: Token-Revoke bei DATEV (AT + RT), dann lokale Daten löschen
   */
  async disconnect(customerId: string, app: DatevApp): Promise<void> {
    // Backend ruft DATEV /revoke auf und löscht dann lokale Daten
    const response = await netlifyFetch(`${this.getApiBaseUrl()}/revoke`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ customerId, app })
    });

    if (!response.ok) {
      throw await parseBackendErrorResponse(response);
    }
  }

  /**
   * Ruft die Liste freigegebener Mandanten für die jeweilige App neu bei DATEV ab.
   * Der Backend-Endpoint cached das Ergebnis in datev_auth.authorized_clients
   * (wird von den datev-{app}-Data-Proxies geschrieben).
   * Wird verwendet für den "Aktualisieren"-Button im Verbindung-Tab.
   */
  /**
   * Ruft die Liste freigegebener Mandanten für die jeweilige App neu bei DATEV ab.
   * Der Backend-Endpoint cached das Ergebnis in datev_auth.authorized_clients
   * (wird von den datev-{app}-Data-Proxies geschrieben).
   * Wird verwendet für den "Aktualisieren"-Button im Verbindung-Tab.
   */
  async refreshAuthorizedClients(
    customerId: string,
    app: DatevApp
  ): Promise<AuthorizedClientsResponse> {
    const endpointByApp: Record<DatevApp, string> = {
      payrollreports: 'datev-payrollreports',
      exports: 'datev-exports',
      exchange: 'datev-exchange',
      eau: 'datev-eau'
    };
    const fn = endpointByApp[app];
    const response = await netlifyFetch(
      `https://intranet.xtax.de/.netlify/functions/${fn}/clients?customerId=${customerId}`
    );
    if (!response.ok) {
      throw await parseBackendErrorResponse(response);
    }
    return response.json();
  }

  /**
   * Pro-Bestand-Berechtigungspruefung via GET /clients/{client-id}.
   * Wird unmittelbar nach dem Verbindungsaufbau aufgerufen, um die DATEV-MUST-
   * Anforderung zu erfuellen: "Hier darf erst dann eine erfolgreiche Verbindung
   * angezeigt werden, wenn die Online-API die Zugriffsrechte auf den
   * Ordnungsbegriff/Datenbestand bestätigt hat."
   *
   * Returns: Aufschluesselung der Bestaende nach Verifikations-Status.
   */
  async verifyClientPermissions(
    customerId: string,
    app: DatevApp,
    clientIds: string[]
  ): Promise<{ verified: string[]; forbidden: string[]; errors: { clientId: string; message: string }[] }> {
    const endpointByApp: Record<DatevApp, string> = {
      payrollreports: 'datev-payrollreports',
      exports: 'datev-exports',
      exchange: 'datev-exchange',
      eau: 'datev-eau'
    };
    const fn = endpointByApp[app];

    const verified: string[] = [];
    const forbidden: string[] = [];
    const errors: { clientId: string; message: string }[] = [];

    for (const clientId of clientIds) {
      try {
        const res = await netlifyFetch(
          `https://intranet.xtax.de/.netlify/functions/${fn}/clients/${clientId}?customerId=${customerId}`
        );
        if (res.ok) {
          verified.push(clientId);
        } else if (res.status === 403) {
          forbidden.push(clientId);
        } else {
          let msg = `HTTP ${res.status}`;
          try {
            const body = await res.json();
            msg = body.message || body.error || msg;
          } catch { /* Body nicht lesbar */ }
          errors.push({ clientId, message: msg });
        }
      } catch (err) {
        errors.push({ clientId, message: err instanceof Error ? err.message : String(err) });
      }
    }

    return { verified, forbidden, errors };
  }

  /**
   * Startet die Synchronisation der Mitarbeiterdaten
   */
  async syncEmployees(
    customerId: string,
    beraternummer: string,
    mandantennummer: string
  ): Promise<SyncResult> {
    try {
      // Zähle aktuelle Mitarbeiter
      const { count: oldCount } = await supabase
        .from('datev_employees')
        .select('*', { count: 'exact', head: true })
        .eq('customer_id', customerId)
        .is('austrittsdatum', null);

      // Starte Sync Job
      const response = await netlifyFetch(`${this.getApiBaseUrl()}/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          beraternummer,
          mandantennummer
        })
      });

      if (!response.ok) {
        const error = await response.json();
        
        // Token abgelaufen? Versuche Refresh
        if (error.needsRefresh || error.requires_reconnect) {
          const refreshResponse = await netlifyFetch(`${this.getApiBaseUrl()}/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ customerId })
          });

          if (refreshResponse.ok) {
            // Versuche Sync erneut
            return this.syncEmployees(customerId, beraternummer, mandantennummer);
          }
        }

        return {
          success: false,
          error: error.error || 'Synchronisation fehlgeschlagen'
        };
      }

      const { jobId } = await response.json();

      // Warte initial 90 Sekunden
      await new Promise(resolve => setTimeout(resolve, 90000));

      // Polling für Job-Status
      let attempts = 0;
      const maxAttempts = 10;

      while (attempts < maxAttempts) {
        const statusResponse = await netlifyFetch(
          `${this.getApiBaseUrl()}/job-status?jobId=${jobId}&customerId=${customerId}&mandantennummer=${mandantennummer}`
        );

        if (statusResponse.ok) {
          const status = await statusResponse.json();

          if (status.status === 'COMPLETED') {
            // Zähle neue Mitarbeiter
            const { count: newCount } = await supabase
              .from('datev_employees')
              .select('*', { count: 'exact', head: true })
              .eq('customer_id', customerId)
              .is('austrittsdatum', null);

            return {
              success: true,
              employeesCount: newCount || 0,
              newEmployees: Math.max(0, (newCount || 0) - (oldCount || 0)),
              updatedEmployees: 0,
              deletedEmployees: Math.max(0, (oldCount || 0) - (newCount || 0))
            };
          }

          if (status.status === 'FAILED') {
            return {
              success: false,
              error: 'DATEV-Export fehlgeschlagen'
            };
          }
        }

        attempts++;
        // Warte 60 Sekunden zwischen Versuchen
        await new Promise(resolve => setTimeout(resolve, 60000));
      }

      return {
        success: false,
        error: 'Timeout bei der Synchronisation'
      };
    } catch (error) {
      console.error('Sync error:', error);
      return {
        success: false,
        error: 'Unerwarteter Fehler bei der Synchronisation'
      };
    }
  }

  /**
   * Lädt die Mitarbeiterdaten aus der lokalen DB
   */
  async getEmployees(customerId: string, activeOnly: boolean = true): Promise<DatevEmployee[]> {
    try {
      let query = supabase
        .from('datev_employees')
        .select('*')
        .eq('customer_id', customerId)
        .order('nachname', { ascending: true });

      if (activeOnly) {
        query = query.is('austrittsdatum', null);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error loading employees:', error);
      return [];
    }
  }

  /**
   * Gibt Informationen zur letzten Synchronisation zurück
   */
  async getLastSyncInfo(customerId: string): Promise<{
    lastSyncAt: Date | null;
    employeeCount: number;
  }> {
    try {
      // Hole letzten erfolgreichen Sync-Job
      const { data: lastJob } = await supabase
        .from('datev_sync_jobs')
        .select('created_at')
        .eq('customer_id', customerId)
        .eq('status', 'COMPLETED')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      // Zähle aktive Mitarbeiter
      const { count } = await supabase
        .from('datev_employees')
        .select('*', { count: 'exact', head: true })
        .eq('customer_id', customerId)
        .is('austrittsdatum', null);

      return {
        lastSyncAt: lastJob ? new Date(lastJob.created_at) : null,
        employeeCount: count || 0
      };
    } catch (error) {
      console.error('Error getting sync info:', error);
      return {
        lastSyncAt: null,
        employeeCount: 0
      };
    }
  }
}

// Singleton Export
export const datevEmployeeService = new DatevEmployeeService();
